import type { ExerciseTemplate } from '@yuan-math/exercises';
import { lcm, roundTo } from '@yuan-math/math';
import {
  choiceExercise,
  decimalTolerance,
  expr,
  leadingTerm,
  numericExercise,
  options,
  signedConstant,
  template,
  trap,
} from './authoring.ts';

/**
 * The real numbers, and the linear machinery Algebra I opens with.
 *
 * One authoring rule runs through the whole file: a distractor must be
 * *produced* by a wrong method rather than merely look wrong. In the
 * fraction-clearing templates that means the answer you get by forgetting to
 * multiply the constant term; in the point-slope templates, the one you get by
 * writing x − (−4) as x − 4. Both are answers a real student arrives at by
 * working, which is what makes them worth diagnosing.
 */

/** Perfect squares to 400, for roots that turn out rational after all. */
const PERFECT_SQUARES = [4, 9, 16, 25, 36, 49, 64, 81, 100, 121, 144, 169, 196, 225, 256, 289, 324, 361, 400];

/** Numbers under 400 that are not perfect squares, so their roots are irrational. */
const NON_SQUARES = [2, 3, 5, 6, 7, 8, 10, 11, 12, 13, 15, 17, 18, 19, 20, 21, 22, 23, 24, 26, 27, 28, 29, 30];

export const ALGEBRA_ONE_LINEAR_TEMPLATES: ExerciseTemplate[] = [
  /* ------------------------------------------------------- real numbers */
  template({
    id: 'real-number-classify',
    conceptId: 'real-numbers',
    description: 'Classify a number into the smallest set that contains it.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      /*
       * At difficulty 3 the candidates are the ones designed to mislead: a
       * root that comes out whole, and a repeating decimal. Both look like the
       * set above the one they are in.
       */
      const kind = rng.pick(
        difficulty === 2
          ? (['negative-integer', 'fraction', 'irrational-root'] as const)
          : (['rational-root', 'repeating-decimal', 'irrational-root'] as const),
      );

      const item = {
        'negative-integer': () => {
          const n = rng.int(2, 20);
          return { latex: `-${n}`, answer: 'Integer', why: `−${n} is whole but negative, so it is an integer — and every integer is also rational and real.` };
        },
        fraction: () => {
          const d = rng.int(2, 9);
          const n = rng.int(1, d - 1);
          return { latex: `\\frac{${n}}{${d}}`, answer: 'Rational', why: `${n}/${d} is one integer over another, which is exactly what rational means.` };
        },
        'irrational-root': () => {
          const n = rng.pick(NON_SQUARES);
          return { latex: `\\sqrt{${n}}`, answer: 'Irrational', why: `${n} is not a perfect square, so √${n} has an endless non-repeating decimal and no fraction equals it.` };
        },
        'rational-root': () => {
          const sq = rng.pick(PERFECT_SQUARES);
          return { latex: `\\sqrt{${sq}}`, answer: 'Natural', why: `${sq} is a perfect square, so √${sq} = ${Math.sqrt(sq)} — a counting number, not an irrational one.` };
        },
        /*
         * Listed rather than computed. Rendering a repeating decimal means
         * knowing which digits sit under the bar, and deriving that from n/d
         * produced `0.6667\overline{2}` — a string that is not a number at
         * all. Six correct ones cover the range the question needs.
         */
        'repeating-decimal': () => {
          const entry = rng.pick([
            { latex: '0.\\overline{3}', fraction: '1/3' },
            { latex: '0.\\overline{6}', fraction: '2/3' },
            { latex: '0.1\\overline{6}', fraction: '1/6' },
            { latex: '0.\\overline{09}', fraction: '1/11' },
            { latex: '0.\\overline{18}', fraction: '2/11' },
            { latex: '0.\\overline{142857}', fraction: '1/7' },
          ]);
          return {
            latex: entry.latex,
            answer: 'Rational',
            why: `A decimal that repeats forever is always a fraction — this one is ${entry.fraction}. Only an endless decimal that *never* repeats escapes the rationals.`,
          };
        },
      }[kind]();

      const { choices, correctId } = options(
        [
          { label: item.answer, correct: true },
          ...(['Natural', 'Integer', 'Rational', 'Irrational'] as const)
            .filter((set) => set !== item.answer)
            .slice(0, 3)
            .map((set) => ({
              label: set,
              misconceptionId: set === 'Irrational' ? 'root-assumed-irrational' : 'number-set-too-narrow',
            })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'What is the smallest of these sets that this number belongs to?',
        latex: item.latex,
        choices,
        correctId,
        summary: item.why,
        hint: 'The sets nest, so more than one contains it. Work out what the number actually equals before deciding.',
      });
    },
  }),

  template({
    id: 'real-number-property',
    conceptId: 'real-numbers',
    description: 'Name the property that justifies a rearrangement.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const c = rng.int(2, 9);

      const cases = [
        {
          latex: `${a}(x + ${b}) = ${a}x + ${a * b}`,
          answer: 'Distributive',
          why: `The ${a} outside was multiplied onto each term inside. That is the distributive property, and expanding a bracket is the only thing it ever is.`,
        },
        {
          latex: `${a} + x = x + ${a}`,
          answer: 'Commutative',
          why: 'The two terms swapped places and nothing else changed. Order does not matter for addition — that is commutativity.',
        },
        {
          latex: `(x + ${a}) + ${b} = x + (${a} + ${b})`,
          answer: 'Associative',
          why: 'The brackets moved but the order did not. Regrouping without reordering is associativity.',
        },
        {
          latex: `${c}x \\cdot 1 = ${c}x`,
          answer: 'Identity',
          why: 'Multiplying by 1 changed nothing, which is what makes 1 the multiplicative identity.',
        },
      ];
      const chosen = rng.pick(cases);

      const { choices, correctId } = options(
        [
          { label: chosen.answer, correct: true },
          ...cases
            .filter((entry) => entry.answer !== chosen.answer)
            .slice(0, 3)
            .map((entry) => ({ label: entry.answer, misconceptionId: 'property-misnamed' })),
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: 'Which property justifies this step?',
        latex: chosen.latex,
        choices,
        correctId,
        summary: chosen.why,
        hint: 'Ask what actually moved. Did the order change, did the brackets change, or did something get multiplied across a bracket?',
      });
    },
  }),

  /* --------------------------------------------- equations with fractions */
  template({
    id: 'equation-clear-denominators',
    conceptId: 'equations-with-fractions',
    description: 'Solve x/a + x/b = c by clearing both denominators.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const a = rng.int(2, difficulty === 4 ? 6 : 4);
      let b = rng.int(2, difficulty === 4 ? 8 : 5);
      if (b === a) b = a + 1;
      const lcd = lcm(a, b);
      // x is chosen as a multiple of the LCD so both fractions — and hence
      // their total — are whole, and the question a student reads has no
      // rounding in it anywhere.
      const answer = lcd * rng.int(2, 9);
      const total = answer / a + answer / b;
      const coefficient = lcd / a + lcd / b;

      /*
       * The right-hand side left unmultiplied: the student clears both
       * fractions but leaves `total` alone, solving coefficient·x = total.
       * The single commonest error in the topic, and the one the lesson warns
       * about by name. Only offered when it divides out whole, since a trap
       * has to be a response a student would actually write.
       */
      const unmultiplied = total / coefficient;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\frac{x}{${a}} + \\frac{x}{${b}} = ${total}`,
        value: answer,
        summary: `The lowest common denominator is ${lcd}. Multiplying every term by it — the ${total} included — gives ${lcd / a}x + ${lcd / b}x = ${total * lcd}, so ${coefficient}x = ${total * lcd} and x = ${answer}.`,
        hint: 'Multiply both sides by the lowest common denominator, and remember the number on the right gets multiplied too.',
        steps: [
          { label: 'Find the lowest common denominator.', latex: `\\text{LCD} = ${lcd}` },
          { label: 'Multiply every term by it.', latex: `${lcd / a}x + ${lcd / b}x = ${total * lcd}` },
          { label: 'Collect the x terms.', latex: `${coefficient}x = ${total * lcd}` },
          { label: 'Divide.', latex: `x = ${answer}` },
        ],
        traps: [
          ...(Number.isInteger(unmultiplied) && unmultiplied !== answer
            ? [trap(unmultiplied, 'term-not-multiplied')]
            : []),
        ],
      });
    },
  }),

  template({
    id: 'equation-fraction-bracket',
    conceptId: 'equations-with-fractions',
    description: 'Solve an equation whose numerator has more than one term.',
    difficulties: [4, 5],
    build: ({ rng }) => {
      const d = rng.int(2, 6);
      // A non-zero constant, or the numerator has only one term and the
      // bracket the template exists to test never arises.
      const c = rng.int(1, 9) * (rng.bool() ? 1 : -1);
      // The right-hand side is chosen first and the solution derived from it,
      // so both are whole without any repair step.
      const rhs = rng.int(2, 9);
      const answer = rhs * d - c;

      return numericExercise({
        prompt: 'Solve for x.',
        latex: `\\frac{x ${signedConstant(c)}}{${d}} = ${rhs}`,
        value: answer,
        summary: `The whole numerator sits over the ${d}, so multiplying both sides by ${d} releases all of it: x ${signedConstant(c)} = ${rhs * d}, and x = ${answer}.`,
        hint: 'Multiplying by the denominator frees every term of the numerator at once, not just the first one.',
        steps: [
          { label: `Multiply both sides by ${d}.`, latex: `x ${signedConstant(c)} = ${rhs * d}` },
          { label: c >= 0 ? `Subtract ${c}.` : `Add ${Math.abs(c)}.`, latex: `x = ${answer}` },
        ],
        // The constant never brought across: the student stops at x + c = rhs·d
        // and reports that value as x.
        traps: [...(rhs * d === answer ? [] : [trap(rhs * d, 'constant-left-in-numerator')])],
      });
    },
  }),

  /* ------------------------------------------------- compound inequalities */
  template({
    id: 'compound-inequality-three-part',
    conceptId: 'compound-inequalities',
    description: 'Solve a three-part inequality and report one of its bounds.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const m = rng.int(2, difficulty === 4 ? 6 : 4);
      const c = rng.int(-9, 9);
      const low = rng.int(-6, 2);
      const high = low + rng.int(2, 8);
      const leftBound = m * low + c;
      const rightBound = m * high + c;
      const wantLower = rng.bool();
      const answer = wantLower ? low : high;

      return numericExercise({
        prompt: `Solve this inequality. What is its ${wantLower ? 'lower' : 'upper'} bound for x?`,
        latex: `${leftBound} < ${expr(leadingTerm(m), signedConstant(c))} < ${rightBound}`,
        value: answer,
        summary: `Subtracting ${c} from all three parts gives ${leftBound - c} < ${m}x < ${rightBound - c}, and dividing all three by ${m} gives ${low} < x < ${high}. The ${wantLower ? 'lower' : 'upper'} bound is ${answer}.`,
        hint: 'Whatever you do, do it to all three parts. The x ends up alone in the middle.',
        steps: [
          { label: `${c >= 0 ? 'Subtract' : 'Add'} ${Math.abs(c)} throughout.`, latex: `${leftBound - c} < ${m}x < ${rightBound - c}` },
          { label: `Divide all three parts by ${m}.`, latex: `${low} < x < ${high}` },
        ],
        // The other bound reported: the two are solved together and confused.
        traps: [...(wantLower ? [trap(high, 'compound-bound-swapped')] : [trap(low, 'compound-bound-swapped')])],
      });
    },
  }),

  template({
    id: 'compound-inequality-and-or',
    conceptId: 'compound-inequalities',
    description: 'Decide what a pair of conditions describes.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const low = rng.int(-5, 3);
      const high = low + rng.int(3, 8);
      const isAnd = rng.bool();

      const { choices, correctId } = options(
        [
          {
            label: isAnd ? `One stretch: every x between ${low} and ${high}` : `Two pieces: x below ${low}, and x above ${high}`,
            correct: true,
          },
          {
            label: isAnd ? `Two pieces: x below ${low}, and x above ${high}` : `One stretch: every x between ${low} and ${high}`,
            misconceptionId: 'and-or-inequality-swapped',
          },
          { label: 'No solution — the two conditions contradict', misconceptionId: 'compound-emptiness-misjudged' },
          { label: 'Every real number satisfies it', misconceptionId: 'compound-emptiness-misjudged' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: isAnd
          ? `What does "x > ${low} and x < ${high}" describe?`
          : `What does "x < ${low} or x > ${high}" describe?`,
        choices,
        correctId,
        summary: isAnd
          ? `"And" needs both conditions at once, so the answer is the overlap — the single stretch between ${low} and ${high}.`
          : `"Or" needs only one condition, so the answer is everything either allows: two separate pieces heading away from each other, with the values between ${low} and ${high} excluded.`,
        hint: 'Does a number have to satisfy both conditions, or just one of them?',
      });
    },
  }),

  /* -------------------------------------------------- forms of a line */
  template({
    id: 'point-slope-write',
    conceptId: 'linear-equation-forms',
    description: 'Read a coordinate out of a point-slope equation, where a negative doubles up.',
    difficulties: [2, 3],
    build: ({ rng, difficulty }) => {
      const m = rng.int(2, 9) * (rng.bool() ? 1 : -1);
      // Negative coordinates at difficulty 3, where the double negative is
      // exactly what the question is testing.
      const x1 = difficulty === 3 ? rng.int(-8, -1) : rng.int(1, 8);
      /*
       * Never zero. `signedConstant(0)` renders nothing, which is correct
       * arithmetic and broken typography: y − 0 prints as `y ` and leaves a
       * double space before the equals sign. It would also make the question
       * degenerate, since the term whose sign is being read has vanished.
       */
      const y1 = rng.int(1, 8) * (rng.bool() ? 1 : -1);
      const wantX = rng.bool();
      const answer = wantX ? x1 : y1;

      return numericExercise({
        prompt: `This line is written in point-slope form. What is the ${wantX ? 'x' : 'y'}-coordinate of the point it was built around?`,
        latex: expr('y', signedConstant(-y1), '=', `${m}(x`, `${signedConstant(-x1)})`),
        value: answer,
        summary: `Point-slope form is y − y₁ = m(x − x₁), so the brackets hold the point subtracted. Here that makes the point (${x1}, ${y1}).`,
        hint: 'The form subtracts both coordinates. A plus sign in the bracket therefore means the coordinate was negative.',
        steps: [
          { label: 'Compare with y − y₁ = m(x − x₁).', latex: `y - (${y1}) = ${m}(x - (${x1}))` },
          { label: 'Read the point.', latex: `(${x1}, ${y1})` },
        ],
        // The sign taken off the page rather than undone: x + 4 read as x1 = 4.
        traps: [...(answer === -answer ? [] : [trap(-answer, 'point-slope-sign-kept')])],
      });
    },
  }),

  template({
    id: 'point-slope-to-intercept',
    conceptId: 'linear-equation-forms',
    description: 'Convert point-slope form to slope-intercept and read the intercept.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const m = rng.int(2, 8) * (rng.bool() ? 1 : -1);
      /*
       * Both coordinates non-zero, for the reason given on the template above:
       * a vanished term prints a stray gap, and an x₁ of 0 would additionally
       * make the point *be* the intercept, so the trap below would grade as
       * the correct answer.
       */
      const x1 = rng.int(1, 6) * (rng.bool() ? 1 : -1);
      const y1 = rng.int(1, 8) * (rng.bool() ? 1 : -1);
      const c = y1 - m * x1;

      return numericExercise({
        prompt: 'Rewrite this line in the form y = mx + c. What is c, its y-intercept?',
        latex: expr('y', signedConstant(-y1), '=', `${m}(x`, `${signedConstant(-x1)})`),
        value: c,
        summary: `Expanding gives y ${signedConstant(-y1)} = ${m}x ${signedConstant(-m * x1)}, and adding ${y1} to both sides gives y = ${m}x ${signedConstant(c)}. The y-intercept is ${c}.`,
        hint: 'Expand the bracket first, then get y on its own. The constant left beside the x term is the intercept.',
        steps: [
          { label: 'Expand the bracket.', latex: `y ${signedConstant(-y1)} = ${m}x ${signedConstant(-m * x1)}` },
          { label: `${y1 >= 0 ? 'Add' : 'Subtract'} ${Math.abs(y1)} to both sides.`, latex: `y = ${m}x ${signedConstant(c)}` },
        ],
        traps: [
          // y1 read as the intercept — true only when the point is on the axis.
          ...(y1 === c ? [] : [trap(y1, 'point-taken-as-intercept')]),
        ],
      });
    },
  }),

  template({
    id: 'standard-form-intercept',
    conceptId: 'linear-equation-forms',
    description: 'Read an intercept off standard form, which is what standard form is for.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const multiple = rng.int(2, 6);
      // A constant divisible by both, so both intercepts are whole and the
      // question is about method rather than arithmetic.
      const c = a * b * multiple;
      const wantX = rng.bool();
      const answer = wantX ? c / a : c / b;
      const other = wantX ? c / b : c / a;

      return numericExercise({
        prompt: `Where does this line cross the ${wantX ? 'x' : 'y'}-axis?`,
        latex: `${a}x + ${b}y = ${c}`,
        value: answer,
        summary: `On the ${wantX ? 'x' : 'y'}-axis, ${wantX ? 'y' : 'x'} is 0. Setting ${wantX ? 'y' : 'x'} = 0 leaves ${wantX ? `${a}x` : `${b}y`} = ${c}, so ${wantX ? 'x' : 'y'} = ${answer}.`,
        hint: `A point on the ${wantX ? 'x' : 'y'}-axis has ${wantX ? 'y' : 'x'} = 0. Substitute that and the other term disappears.`,
        steps: [
          { label: `Set ${wantX ? 'y' : 'x'} to zero.`, latex: wantX ? `${a}x = ${c}` : `${b}y = ${c}` },
          { label: 'Divide.', latex: `${wantX ? 'x' : 'y'} = ${answer}` },
        ],
        // The other intercept: the right method applied to the wrong variable.
        traps: [...(other === answer ? [] : [trap(other, 'wrong-intercept-read')])],
      });
    },
  }),

  /* --------------------------------------------------- standard deviation */
  template({
    id: 'standard-deviation-compute',
    conceptId: 'standard-deviation',
    description: 'Compute a standard deviation from five values.',
    difficulties: [3, 4],
    build: ({ rng, difficulty }) => {
      const mean = rng.int(8, difficulty === 4 ? 40 : 20);
      const spread = difficulty === 3 ? 4 : 7;
      /*
       * Deviations forced to sum to zero, so `mean` really is the mean and the
       * question can state it. The first is kept away from zero for the same
       * reason the MAD template does it: five identical values give a standard
       * deviation of 0, which collides with the "deviations cancelled" trap.
       */
      const first = [
        rng.int(1, spread) * (rng.bool() ? 1 : -1),
        ...Array.from({ length: 3 }, () => rng.int(-spread, spread)),
      ];
      const last = -first.reduce((sum, d) => sum + d, 0);
      const deviations = [...first, last];
      const values = deviations.map((d) => mean + d);
      const squares = deviations.map((d) => d * d);
      const sumOfSquares = squares.reduce((sum, s) => sum + s, 0);
      const variance = sumOfSquares / 5;
      const answer = roundTo(Math.sqrt(variance), 2);

      return numericExercise({
        prompt: `The mean of ${values.join(', ')} is ${mean}. What is the standard deviation? Give your answer to two decimal places.`,
        value: answer,
        tolerance: decimalTolerance(answer, 2),
        summary: `The deviations from ${mean} are ${deviations.join(', ')}. Squaring gives ${squares.join(', ')}, which total ${sumOfSquares}; dividing by 5 gives a variance of ${roundTo(variance, 2)}, and its square root is ${answer}.`,
        hint: 'Square each deviation, average the squares, then take the square root of that average.',
        steps: [
          { label: `Deviation of each value from ${mean}.`, latex: deviations.join(', \\; ') },
          { label: 'Square each one.', latex: squares.join(', \\; ') },
          { label: 'Add them and divide by 5 — the variance.', latex: `\\frac{${sumOfSquares}}{5} = ${roundTo(variance, 2)}` },
          { label: 'Take the square root.', latex: `\\sqrt{${roundTo(variance, 2)}} \\approx ${answer}` },
        ],
        traps: [
          // Stopped at the variance, in squared units.
          ...(roundTo(variance, 2) === answer ? [] : [trap(roundTo(variance, 2), 'variance-not-rooted')]),
          // Deviations averaged without squaring, so they cancelled to nothing.
          trap(0, 'deviations-not-absolute'),
          // The squares totalled but never averaged.
          ...(sumOfSquares === answer ? [] : [trap(sumOfSquares, 'mean-wrong-divisor')]),
        ],
      });
    },
  }),

  template({
    id: 'standard-deviation-compare',
    conceptId: 'standard-deviation',
    description: 'Compare two data sets that share a mean but not a spread.',
    difficulties: [2, 3],
    build: ({ rng }) => {
      const mean = rng.int(10, 30);
      const tight = rng.int(1, 2);
      const wide = rng.int(5, 9);
      const tightSet = [mean - tight, mean, mean, mean, mean + tight];
      const wideSet = [mean - wide, mean - wide + 1, mean, mean + wide - 1, mean + wide];
      const wideIsA = rng.bool();
      const [setA, setB] = wideIsA ? [wideSet, tightSet] : [tightSet, wideSet];

      const { choices, correctId } = options(
        [
          { label: `Set ${wideIsA ? 'A' : 'B'}`, correct: true },
          { label: `Set ${wideIsA ? 'B' : 'A'}`, misconceptionId: 'spread-judged-by-centre' },
          { label: 'They are the same — both have the same mean', misconceptionId: 'spread-judged-by-centre' },
          { label: 'Neither — standard deviation cannot compare two sets', misconceptionId: 'spread-judged-by-centre' },
        ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: `Set A is ${setA.join(', ')} and Set B is ${setB.join(', ')}. Both have a mean of ${mean}. Which has the greater standard deviation?`,
        choices,
        correctId,
        summary: `Standard deviation measures distance from the mean, and the mean says nothing about it. Set ${wideIsA ? 'A' : 'B'} reaches ${wide} from the mean while the other stays within ${tight}, so it is the more spread out of the two.`,
        hint: 'The means are identical, so that is not the question. How far from the mean does each set actually reach?',
      });
    },
  }),

  template({
    id: 'standard-deviation-meaning',
    conceptId: 'standard-deviation',
    description: 'Say what a standard deviation of zero means, or what the units of a variance are.',
    difficulties: [3, 4],
    build: ({ rng }) => {
      const zeroCase = rng.bool();
      const value = rng.int(2, 12);
      const mean = rng.int(10, 40);

      const { choices, correctId } = options(
        zeroCase
          ? [
              { label: 'Every value in the set is identical', correct: true },
              { label: 'The values are evenly spread out', misconceptionId: 'zero-deviation-misread' },
              { label: 'The mean of the set is zero', misconceptionId: 'zero-deviation-misread' },
              { label: 'The values cancel out above and below the mean', misconceptionId: 'deviations-not-absolute' },
            ]
          : [
              { label: `${value} marks`, correct: true },
              { label: `${value * value} marks`, misconceptionId: 'variance-not-rooted' },
              { label: `${value} marks²`, misconceptionId: 'variance-not-rooted' },
              { label: `${roundTo(Math.sqrt(value), 2)} marks`, misconceptionId: 'variance-not-rooted' },
            ],
        rng.shuffle,
      );

      return choiceExercise({
        prompt: zeroCase
          ? `A set of ${rng.int(5, 20)} test scores has a mean of ${mean} and a standard deviation of 0. What does that tell you?`
          : `A set of test scores has a variance of ${value * value} marks². What is its standard deviation?`,
        choices,
        correctId,
        summary: zeroCase
          ? 'A standard deviation is an average distance from the mean, so it can only be zero when every distance is zero — which means every value equals the mean. It is the smallest a standard deviation can ever be.'
          : `Variance is the value before the square root, and its units are squared. Taking the root gives √${value * value} = ${value}, back in marks where it can be compared with the scores themselves.`,
        hint: zeroCase
          ? 'Standard deviation is an average distance from the mean. What has to be true for an average of distances to come out as zero?'
          : 'Variance is one step short of the answer. What is the last step of the calculation, and what does it do to the units?',
      });
    },
  }),
];
